import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, FormRadioGroup, InPageAlert, SectionPage, Select } from '@snsw/react-component-library';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { useSelector } from 'react-redux';
import { findPlantRefByPlantTypeCode, PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { mapRefDataPickList, selectedTextFromOptions } from 'shared/utils/Helpers';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';
import { DesignAlterationDetails } from './DesignAlterationDetails';

export const PlantTypeDesignDetails = React.forwardRef((props, ref) => {
  const savedPlantDesign = useSelector((state) => state.plantDesign);

  const [plantType, setPlantType] = useState(savedPlantDesign.plantType);
  const [subType, setSubType] = useState(savedPlantDesign.subType);
  const [otherSubType, setOtherSubType] = useState(savedPlantDesign.otherSubType);
  const [registrationType, setRegistrationType] = useState(savedPlantDesign.registrationType);
  const [alterationDetails, setAlterationDetails] = useState(savedPlantDesign.alterationDetails);

  const [plantRef, setPlantRef] = useState(undefined);
  const [subTypeOptions, setSubTypeOptions] = useState(undefined);
  const [isCorrection, setIsCorrection] = useState(false);

  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);
  const [alterationDetailsErrorSummary, setAlterationDetailsErrorSummary] = useState([]);

  const alterationDetailsRef = React.useRef();
  const PAGE_CONSTANTS = PlantDesignDetailsPage.common;
  const REG_TYPES = PAGE_CONSTANTS.registrationTypes;

  const plantTypeOptions = mapRefDataPickList(
    props.refData[PlantDesignRefData.plantTypes.name],
    PlantDesignRefData.plantTypes.pickListValueName,
  );

  const registrationTypeOptions = [REG_TYPES.new, REG_TYPES.alteration, REG_TYPES.corrections];

  useEffect(() => {
    props.onErrorChange([...errorSummary, ...alterationDetailsErrorSummary]);
  }, [errorSummary, alterationDetailsErrorSummary]);

  useEffect(() => {
    setPlantRef(findPlantRefByPlantTypeCode(plantType));
  }, [plantType]);

  useEffect(() => {
    let plantName = plantRef?.name;
    if (!plantName || !registrationType) return;

    if (registrationType === REG_TYPES.new.value) {
      setAlterationDetails({});
    }

    if (registrationType === REG_TYPES.corrections.value) {
      setIsCorrection(true);
      return;
    }

    setIsCorrection(false);
  }, [plantRef, registrationType]);

  useEffect(() => {
    props.setCanContinue(!isCorrection);
    props.setIsCorrection(isCorrection);
  }, [isCorrection]);

  useEffect(() => {
    const subTypeOptionsKey = plantRef?.subTypeRef?.optionsKey;
    if (!subTypeOptionsKey) {
      setSubTypeOptions(null);
      return;
    }
    const options = mapRefDataPickList(props.refData[subTypeOptionsKey]);
    setSubTypeOptions(options);
  }, [plantRef]);

  const hasSubTypeFilled = () => {
    if (!subTypeOptions) return true; // The plant type has no subtypes.

    // If 'Other' subtype is selected, check if it's specified.
    if (subType === plantRef?.subTypeRef?.otherSubTypeValue) {
      return subType && otherSubType;
    }

    return subType;
  };

  const validators = [
    {
      fieldName: 'plantType',
      fieldValue: plantType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'subType',
      fieldValue: subType,
      isValid: (v) => !isEmpty(v) || !subTypeOptions,
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'otherSubType',
      fieldValue: otherSubType,
      isValid: (v) => !isEmpty(v) || subType !== plantRef?.subTypeRef?.otherSubTypeValue,
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'registrationType',
      fieldValue: registrationType,
      isValid: (v) => !isEmpty(v) || !plantType || (subTypeOptions && !subType),
      errorMessage: () => 'Please select an option',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isValid = validateExt(validators, setErrors, setErrorSummary);
        if (registrationType === REG_TYPES.alteration.value) {
          isValid = isValid && alterationDetailsRef?.current?.validate();
        }
        return isValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      plantType,
      plantTypeDescription: selectedTextFromOptions(plantTypeOptions, plantType),
      subType,
      otherSubType,
      subTypeDescription: selectedTextFromOptions(subTypeOptions, subType),
      registrationType,
      alterationDetails,
    };

    props.onDetailsChange(details);

    if (props.showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [plantType, subType, otherSubType, registrationType, alterationDetails, subTypeOptions]);

  return (
    <>
      <SectionPage>
        <FormGroup
          id="plantType"
          label={PAGE_CONSTANTS.plantType.label}
          errorMessage={errors?.plantType?.errorMessage}
          hasError={props.showErrors && errors?.plantType?.invalid}
        >
          <Select
            options={plantTypeOptions}
            onChange={(e) => {
              setPlantType(e.target.value);
              setSubType('');
              setRegistrationType('');
            }}
            value={plantType}
            inputWidth="xl"
          />
        </FormGroup>

        {plantType && subTypeOptions && (
          <RadioOrDropdownSelector
            name="subType"
            options={subTypeOptions}
            title={`Type of ${plantRef.name}`}
            errorMessage={errors?.subType?.errorMessage}
            hasError={props.showErrors && errors?.subType?.invalid}
            onChange={(v) => {
              setSubType(v);
              setOtherSubType('');
            }}
            value={subType}
            showExtraInput={subType === plantRef.subTypeRef?.otherSubTypeValue}
            extraInputType="textarea"
            onChangeExtraInput={setOtherSubType}
            extraInputValue={otherSubType}
            extraInputErrorMessage={errors?.otherSubType?.errorMessage}
            extraInputHasError={props.showErrors && errors?.otherSubType?.invalid}
            extraInputTitle={`Please describe the type of ${plantRef.name}`}
            maxOptionsLengthForRadio={5}
            margin={{ top: 'lg' }}
          />
        )}

        {plantRef && hasSubTypeFilled() && (
          <>
            {plantRef?.helpText && (
              <InPageAlert
                variant="info"
                title={plantRef.helpText.title}
                headingLevel="h5"
                className={styles.marginTop2rem}
              >
                {plantRef.helpText.paragraphs.map((p) => (
                  <p className={styles.helpTextParagraph} key={p}>
                    {p}
                  </p>
                ))}

                <ul className={styles.helpTextList}>
                  {plantRef.helpText.listItems.map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              </InPageAlert>
            )}

            <FormRadioGroup
              id="registrationType"
              name="registrationType"
              vertical={true}
              options={registrationTypeOptions}
              onChange={(e) => setRegistrationType(e)}
              value={registrationType}
              errorMessage={errors?.registrationType?.errorMessage}
              hasError={props.showErrors && errors?.registrationType?.invalid}
              legend={PAGE_CONSTANTS.registrationTypeLabel}
              margin={{ top: 'lg' }}
            />

            {registrationType && (
              <>
                {registrationType === REG_TYPES.alteration.value && (
                  <DesignAlterationDetails
                    ref={alterationDetailsRef}
                    plantType={plantType}
                    subType={subType}
                    plantRef={plantRef}
                    onDetailsChange={setAlterationDetails}
                    onErrorChange={setAlterationDetailsErrorSummary}
                    showErrors={props.showErrors}
                    setCanContinue={props.setCanContinue}
                  ></DesignAlterationDetails>
                )}

                {isCorrection && (
                  <InPageAlert
                    variant="warning"
                    title="You need to contact SafeWork NSW to make a correction."
                    headingLevel="h5"
                  >
                    Close this window and email your correction to{' '}
                    <a href="mailto:licensing@safework.nsw.gov.au">licensing@safework.nsw.gov.au</a>
                    <br></br>
                    We will not save details entered in this application.
                  </InPageAlert>
                )}
              </>
            )}
          </>
        )}
      </SectionPage>
    </>
  );
});

PlantTypeDesignDetails.displayName = 'PlantDesignDetailsCommon';

PlantTypeDesignDetails.propTypes = {
  plantType: PropTypes.string,
  refData: PropTypes.object,
  onChange: PropTypes.func,
  onErrorChange: PropTypes.func,
  showErrors: PropTypes.bool,
  setCanContinue: PropTypes.func,
};
